import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { delay, distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { combineLatestWith, Observable, switchMap } from 'rxjs';
import { AutocompleteMarker, DefaultMapMarker, getAutocompleteMarkerUrl, MarkerLocation } from './map-utils';
import { Dictionary } from '@ngrx/entity';
import { CCRowSetting } from '../../../models/locations-models/cc-row-setting';
import { Location } from '../../../models/locations-models/location';
import { isCcCandidate } from '../location-utils';
import { cloneDeep, isEqual } from 'lodash';
import { concatLatestFrom } from '@ngrx/operators';
import * as locationListSelectors from '../location-list/state/location-list-selectors';
import * as mapSelectors from './state/map-selectors';
import * as ccListSelectors from '../cc-list/state/cc-list-selectors';
import { selectIsCcOn } from '../state/location-skeleton-combined-selectors';
import { selectMarker, selectMarkersForSelectedLocations } from './state/map-combined.selectors';

export interface MapState {
  // TODO: does not need to be stored, rather store infoWindowMarkerId, then create a selector (or computed signal)
  infoWindowLocations: MarkerLocation[] | undefined;
  hoveredMarker: DefaultMapMarker | AutocompleteMarker;
  tooltipX: number;
  tooltipY: number;
  // true when bounds change, idle sets to false - avoid triggering clustering or marker rendering
  // while camera is panning or zooming
  // https://mapsplatform.google.com/resources/blog/google-maps-platform-best-practices-optimization-and-performance-tips/
  mapLoading: boolean;
  autocompleteMarker: AutocompleteMarker; // marker that gets placed on the map if user
  // successfully searched for a location using map autocomplete
}

export interface MapMarkerTooltipViewModel {
  hoveredMarker: DefaultMapMarker | AutocompleteMarker;
  tooltipX: number;
  tooltipY: number;
}

export interface MapViewModel {
  hoveredMarker: DefaultMapMarker | AutocompleteMarker;
  isCcOn: boolean;
  ccRadius: number;
  focusedLocation: Location;
  ccRowSettings: Dictionary<CCRowSetting>;
}

export interface InfoWindowViewModel {
  focusedLocationId: number,
  hoveredRowId: number,
  infoWindowLocations: MarkerLocation[],
  ccRowSettingsDictionary: Dictionary<CCRowSetting>,
}

@Injectable()
export class MapLocalStore extends ComponentStore<MapState> {
  constructor(
      private store: Store) {
    super({
      infoWindowLocations: undefined,
      hoveredMarker: null,
      tooltipX: null,
      tooltipY: null,
      mapLoading: false,
      autocompleteMarker: null,
    });
    // update existing autocompleteMarker with each minimize markers / cv marker hover change
    this.upsertAutocompleteMarker$(this.store.select(mapSelectors.selectAreMarkersMinimized));
  }

  // SELECTORS

  infoWindowLocations$: Observable<MarkerLocation[]> = this.select(state => state.infoWindowLocations);
  hoveredMarker$: Observable<DefaultMapMarker | AutocompleteMarker> = this.select(state => state.hoveredMarker);
  mapLoading$: Observable<boolean> = this.select(state => state.mapLoading);
  autocompleteMarker$: Observable<AutocompleteMarker> = this.select(state => state.autocompleteMarker);

  infoWindowViewModel$: Observable<InfoWindowViewModel> = this.select({
    focusedLocationId: this.store.select(locationListSelectors.selectFocusedRowId),
    hoveredRowId: this.store.select(locationListSelectors.selectHoveredRowId),
    infoWindowLocations: this.infoWindowLocations$,
    ccRowSettingsDictionary: this.store.select(ccListSelectors.selectCcRowSettingsDictionary),
  });

  mapViewModel$: Observable<MapViewModel> = this.select({
    hoveredMarker: this.hoveredMarker$,
    isCcOn: this.store.select(selectIsCcOn),
    ccRadius: this.store.select(ccListSelectors.selectCcRadius),
    focusedLocation: this.store.select(locationListSelectors.selectFocusedLocation),
    ccRowSettings: this.store.select(ccListSelectors.selectCcRowSettingsDictionary),
  });

  // Separate observable so only a change to selectMarkersForSelectedLocations can trigger a new
  // emission (we do not want to trigger clustering logic needlessly).
  // Delayed so bounds are first fitted (zooms out) before pushing new marker data = higher zoom
  // means higher chance of existing markers being hidden (nested in existing clusters) = better performance
  // Will hold off next value while map is loading (can freeze up otherwise)
  delayedAndFilteredMarkers$: Observable<Array<DefaultMapMarker | AutocompleteMarker>> = this.store.select(selectMarkersForSelectedLocations).pipe(
      // Since locations with invalid coords are being filtered out in the selector, it might
      // happen that the next DefaultMapMarker[] is virtually the same as the last one which causes
      // markers nested in clusters to change visibility? and thus both the marker and its
      // cluster are visible unless this check is here ... prone to refactoring
      distinctUntilChanged((x, y) => {
        if (x.length !== y.length) {
          // different arr length - arrays are distinct
          return false;
        }
        for (let i = 0; i < x.length; i++) {
          if (!isEqual(x[i], y[i])) {
            // objects are different - arrays are distinct
            return false;
          }
        }
        return true;
      }),
      delay(50),
      switchMap((markers) => this.mapLoading$.pipe(
          filter((mapLoading) => !mapLoading),
          map(() => markers))),
      // add autocomplete marker
      combineLatestWith(this.autocompleteMarker$),
      map(([markers, autocompleteMarker]) =>
          autocompleteMarker ? [...markers, autocompleteMarker as any] : markers));

  tooltipViewModel$: Observable<MapMarkerTooltipViewModel> = this.select(state => {
    return {
      hoveredMarker: state.hoveredMarker,
      tooltipX: state.tooltipX,
      tooltipY: state.tooltipY,
    };
  });

  // UPDATERS

  readonly updateInfoWindowLocations = this.updater((state, infoWindowLocations: MarkerLocation[]): MapState => (
      { ...state, infoWindowLocations: infoWindowLocations }
  ));

  // should there be need for tooltip reactivity, refactor as effect (similar to updateInfoWindowMarker)
  readonly updateHoveredMarker = this.updater((state, hoveredMarker: DefaultMapMarker | AutocompleteMarker): MapState => (
      { ...state, hoveredMarker }
  ));

  readonly updateTooltipX = this.updater((state, tooltipX: number): MapState => (
      { ...state, tooltipX }
  ));

  readonly updateTooltipY = this.updater((state, tooltipY: number): MapState => (
      { ...state, tooltipY }
  ));

  readonly updateMapLoading = this.updater((state, mapLoading: boolean): MapState => (
      { ...state, mapLoading }
  ));

  readonly updateAutocompleteMarker = this.updater((state, marker: AutocompleteMarker): MapState => (
      { ...state, autocompleteMarker: marker }
  ));

  // EFFECTS

  /**
   * Makes sure the info window content is reactive by updating markerLocations from the underlying
   * marker observable
   */
  readonly updateInfoWindowMarker$ = this.effect((id: Observable<string>) => {
    // listen to user selection
    return id.pipe(
        switchMap((id: string) => {
          // get marker observable
          return this.store.select(selectMarker(id))
              .pipe(
                  // add metadata
                  concatLatestFrom(() => this.store.select(locationListSelectors.selectFocusedLocation)),
                  tap(([marker, focusedLocation]) => {
                    // effectively closes the info window by deleting underlying data (non-marker click)
                    if (!marker) {
                      this.updateInfoWindowLocations([]);
                      return;
                    }

                    const infoWindowLocations = cloneDeep(marker.markerLocations);

                    const focusedLocationIndex = focusedLocation ? infoWindowLocations
                        .findIndex(ml => ml.location.id === focusedLocation.id) : -1;

                    const reorderedMarkerLocations: MarkerLocation[] = [];

                    // if there's a focused location, it should always be placed on top
                    if (focusedLocationIndex > -1) {
                      reorderedMarkerLocations.push(...infoWindowLocations.splice(focusedLocationIndex, 1));
                    }

                    // if there are cc-candidates they should be placed next, then all the other locations
                    const ccCandidates = infoWindowLocations.filter((ml, index, arr) => {
                      if (isCcCandidate(ml.location)) {
                        arr.splice(index, 1);
                        return true;
                      }
                      return false;
                    });
                    reorderedMarkerLocations.push(...ccCandidates, ...infoWindowLocations);

                    // update underlying data for the info window to display
                    this.updateInfoWindowLocations(reorderedMarkerLocations);
                  }),
              );
        }),
    );
  });



  /**
   * update CV markers on minimized / hovered marker changes
   */
  readonly upsertAutocompleteMarker$ = this.effect((minimize: Observable<boolean>) => {
    return minimize.pipe(
        combineLatestWith(this.hoveredMarker$),
        // add metadata - does not trigger this effect by itself
        concatLatestFrom(() => this.autocompleteMarker$),
        tap(([[minimize, hoveredMarker], autocompleteMarker]) => {
          if (!autocompleteMarker) return;

          const autocompleteMarkerCopy = cloneDeep(autocompleteMarker);

          autocompleteMarkerCopy.options.icon = {
            url: getAutocompleteMarkerUrl(minimize &&
                hoveredMarker?.id !== autocompleteMarker.id),
          };

          this.updateAutocompleteMarker(autocompleteMarkerCopy);
        }));
  });

}
